import { add } from 'date-fns';

import { useGetAccount } from 'apps-common/hooks/useGetAccount';
import { useGetMembershipOfferings } from 'apps-common/hooks/useGetMembershipOfferings';
import { throwError } from 'apps-common/utils/errorHandler';
import { getDate } from 'apps-common/utils/helpers';
import { isUserInactivePrepaidPending, membershipOfferingPrepaidMonths } from 'apps-common/utils/member';

interface NextPaymentData {
  nextPaymentDateFromSubscription?: Date;
  prepaidMonthsFromOfferings?: number;
  nextPaymentDate: Date;
}

interface LoadingResult {
  isFetching: true;
}

export interface SuccessResult extends NextPaymentData {
  isFetching: false;
}

type HookResult = LoadingResult | SuccessResult;

/** This hook's sole responsibility is to determine the Next Payment Date for expired subscription, by applying any pending prepaid months**/
export const useGetNextPaymentDate = (): HookResult => {
  const { data: accountData, isFetching: isFetchingAccountData } = useGetAccount();
  const { data: offerings, isFetching: isFetchingMembershipOfferings } = useGetMembershipOfferings();

  if (isFetchingAccountData || isFetchingMembershipOfferings)
    return {
      isFetching: true,
    };

  const {
    account: { currentSubscription },
  } = accountData!;

  if (!currentSubscription) {
    throwError('hubGeneralError', 'No subscription found for renewing user.');
  }

  // only expired user has null nextPaymentDate
  if (accountData?.account.currentSubscription?.nextPaymentDate) {
    return {
      nextPaymentDateFromSubscription: new Date(accountData.account.currentSubscription.nextPaymentDate),
      nextPaymentDate: new Date(accountData.account.currentSubscription.nextPaymentDate),
      isFetching: false,
      prepaidMonthsFromOfferings: 0, // no pending prepaid months for active users
    } as SuccessResult;
  }

  const prepaidMonthsFromOfferings = membershipOfferingPrepaidMonths(offerings);
  const nextPaymentDateFromSubscription = getDate(currentSubscription.nextPaymentDate);

  // only adds prepaid months for inactive user
  const nextPaymentDate = add(nextPaymentDateFromSubscription, {
    months: isUserInactivePrepaidPending(currentSubscription, offerings) ? prepaidMonthsFromOfferings : 0,
  });

  return {
    nextPaymentDate,
    nextPaymentDateFromSubscription,
    prepaidMonthsFromOfferings,
    isFetching: false,
  };
};
